import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/query/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";
import { CopilotMessage } from "../features/copilot/CopilotProvider";

export const copilotApi = createApi({
  reducerPath: "copilot",
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getMessages: builder.query<CopilotMessage[], string | SkipToken>({
      queryFn: () => ({ data: [] }),
      async onCacheEntryAdded(organizationId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        if (typeof organizationId !== "string" || !organizationId) return;
        
        let unsubscribe = () => {};
        
        try {
          await cacheDataLoaded;
          
          const messagesRef = collection(db, `organizations/${organizationId}/copilot_messages`);
          const q = query(messagesRef, orderBy("createdAt", "desc"), limit(30));

          unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            })) as CopilotMessage[];
            
            updateCachedData(() => {
              return fetchedMessages.reverse();
            });
          });
        } catch {
          // no-op in case cacheEntryRemoved resolves before cacheDataLoaded,
          // in which case cacheDataLoaded will throw
        }
        
        await cacheEntryRemoved;
        unsubscribe();
      }
    }),
    askCopilot: builder.mutation<void, { organizationId: string; text: string }>({
      queryFn: async ({ organizationId, text }) => {
        try {
          const functions = getFunctions();
          const askCopilotFn = httpsCallable(functions, "askCopilot");
          await askCopilotFn({ text, organizationId });
          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      }
    })
  })
});

export const { useGetMessagesQuery, useAskCopilotMutation } = copilotApi;
